import { hashtagsFor } from "@/tools/seo/textUtils";

// Template-based social caption generator. No LLM call: structured templates per tone and platform, the topic
// (kept exactly as typed) and an optional specific detail, plus hashtags taken from the topic itself.
//
// Every template puts the topic in a slot that reads naturally whether it is a noun phrase ("summer menu") or a
// verb phrase ("launching v2 of my app"): after a colon, as its own sentence, or in quotes. Templates never graft it
// into the middle of a grammatical frame, which is what produced awkward results like "Hot take: launching v2 is more
// important than people realize".

type Platform = "instagram" | "tiktok" | "x" | "linkedin";
type Tone = "fun" | "professional" | "bold" | "genz";

const T: Record<Tone, Record<Platform, string[]>> = {
  fun: {
    instagram: ["Today's little joy: {topic} ✨", "Save this one: {topic} 🤍", "Swipe for the details on {topic} 🎉"],
    tiktok: ["POV: {topic} 👀", "Okay, but have you tried this? {topic} 🎬", "Stay till the end: {topic} 😄"],
    x: ["Today: {topic} 🎉", "{topic}. Thoughts?", "Small win to share: {topic} ✨"],
    linkedin: ["Sharing something I'm excited about: {topic}", "A quick update: {topic}", "Here's what I've been up to: {topic}"],
  },
  professional: {
    instagram: ["{topic}. Details below.", "Introducing: {topic}.", "Now available: {topic}."],
    tiktok: ["Quick overview: {topic}.", "What to know: {topic}.", "Behind the scenes: {topic}."],
    x: ["{topic}.", "New: {topic}.", "Worth a look: {topic}."],
    linkedin: [
      "I'm pleased to share an update: {topic}.",
      "A topic on my mind this week: {topic}. Here is what I've learned, and I'd value your perspective.",
      "Update: {topic}. If this is relevant to your work, let's connect.",
    ],
  },
  bold: {
    instagram: ["{topic}. No half measures. 🔥", "Make it count: {topic} 💥", "This is the moment: {topic} 🚀"],
    tiktok: ["Stop scrolling. {topic} 🔥", "Watch this: {topic} 💥", "{topic}. Let's go. 🚀"],
    x: ["{topic}. Full stop. 🔥", "Not asking. Announcing: {topic} 💥", "Here it is: {topic} 🚀"],
    linkedin: ["{topic}. Here's why it matters.", "Stop waiting. {topic}.", "A direct take: {topic}."],
  },
  genz: {
    instagram: ["not me being obsessed with this: {topic} 😭", "main character energy: {topic} ✨", "bestie, look: {topic} 💅"],
    tiktok: ["it's giving: {topic} 💀", "wait this is actually good: {topic} 😳", "me every day: {topic} fr fr"],
    x: ["{topic}. that's it that's the tweet.", "no thoughts, just: {topic}", "plot twist: {topic} 👀"],
    // Slang on LinkedIn reads as a joke on a professional network, so this falls back to a light, plain tone.
    linkedin: ["Sharing something I'm excited about: {topic}", "A quick update: {topic}", "Here's what I've been up to: {topic}"],
  },
};

const CTA: Record<Platform, string> = {
  instagram: "Link in bio.",
  tiktok: "Follow for more.",
  x: "",
  linkedin: "What do you think?",
};

// Hashtags come from the topic itself (see hashtagsFor); this is how many each platform gets.
const TAG_COUNT: Record<Platform, number> = { instagram: 5, tiktok: 4, x: 2, linkedin: 3 };
const FIXED_TAGS: Partial<Record<Platform, string[]>> = { tiktok: ["#fyp"] };

export interface CaptionOptions { detail?: string; cta?: boolean }

export function generateCaptions(
  topic: string,
  platform: Platform | string = "instagram",
  tone: Tone | string = "fun",
  opts: CaptionOptions = {},
): string[] {
  const p = (platform in TAG_COUNT ? platform : "instagram") as Platform;
  const tn = (tone in T ? tone : "fun") as Tone;
  const clean = topic.trim().replace(/[.!?…]+$/, ""); // the templates add their own end punctuation
  const detail = (opts.detail ?? "").trim();
  const topicTags = hashtagsFor(topic, 12);
  const tags = [...(FIXED_TAGS[p] ?? []), ...topicTags].slice(0, TAG_COUNT[p]);

  return T[tn][p].map((tpl) => {
    let body = tpl.replace(/\{topic\}/g, clean);
    if (detail) body += `\n${/[.!?]$/.test(detail) ? detail : detail + "."}`;
    if (opts.cta && CTA[p]) body += `\n${CTA[p]}`;
    // X drafts carry no hashtags: they use up the 280 characters and the About and FAQ say they are dropped.
    return tags.length && p !== "x" ? `${body}\n\n${tags.join(" ")}` : body;
  });
}
